import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  MapPinIcon,
  BuildingOffice2Icon,
  StarIcon
} from '@heroicons/react/24/outline'

// Components
import SEO from '@components/common/SEO'
import SearchBar from '@components/common/SearchBar'
import { Button } from '@components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card'
import CompanyCard from '@components/company/CompanyCard'
import { CustomAdBanner } from '@components/ads'

// Hooks
import { useFeaturedCompanies } from '@/hooks/useApi'
import { useAuth } from '@/contexts/AuthContext'

// Types
import type { FC } from 'react'

const HomePage: FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, isGuest } = useAuth()

  // Fetch featured companies from API (show 6 companies on homepage)
  const { data: featuredCompanies, isLoading: featuredLoading, error: featuredError } = useFeaturedCompanies(6);

  const features = [
    {
      icon: MagnifyingGlassIcon,
      title: 'ค้นหาง่าย',
      description: 'ค้นหาบริษัทรักษาความปลอดภัยด้วยชื่อ พื้นที่ หรือประเภทบริการ'
    },
    {
      icon: ShieldCheckIcon,
      title: 'ข้อมูลเชื่อถือได้',
      description: 'ข้อมูลบริษัทที่ผ่านการตรวจสอบและปรับปรุงอย่างสม่ำเสมอ'
    },
    {
      icon: MapPinIcon,
      title: 'ครอบคลุมทั่วไทย',
      description: 'รวบรวมบริษัทรักษาความปลอดภัยจากทุกจังหวัดในประเทศไทย'
    }
  ]

  const popularSearches = [
    'รักษาความปลอดภัย กรุงเทพ',
    'รปภ. โรงงาน',
    'ยามรักษาการณ์ คอนโด',
    'รักษาความปลอดภัย อาคาร'
  ]

  return (
    <>
      {/* SEO */}
      <SEO
        title="หน้าแรก"
        description="ค้นหาบริษัทรักษาความปลอดภัยที่เชื่อถือได้ในประเทศไทย พร้อมข้อมูลติดต่อ บริการ และแผนที่ตำแหน่ง"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-primary-100 py-16 md:py-24">
        <div className="container-wrapper">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 mb-6">
                ค้นหา
                <span className="text-primary-600"> บริษัทรักษาความปลอดภัย </span>
                ในไทย
              </h1>

              <p className="text-xl md:text-2xl text-neutral-600 mb-8 leading-relaxed">
                แหล่งรวมข้อมูลบริษัทรักษาความปลอดภัยที่เชื่อถือได้
                <br className="hidden md:block" />
                พร้อมข้อมูลติดต่อครบถ้วน
              </p>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-2xl mx-auto mb-8"
            >
              <SearchBar
                size="lg"
                variant="floating"
                placeholder="ค้นหาบริษัทรักษาความปลอดภัย เช่น กรุงเทพ, รปภ. โรงงาน"
                autoFocus
              />
            </motion.div>

            {/* Popular Searches */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-2 md:gap-3"
            >
              <span className="text-sm text-neutral-600 mr-2">ค้นหายอดนิยม:</span>
              {popularSearches.map((search, index) => (
                <button
                  key={index}
                  className="text-sm text-primary-600 hover:text-primary-800 hover:underline transition-colors duration-200"
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(search)}`)
                  }}
                >
                  {search}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20">
        <div className="container-wrapper">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              ทำไมต้องเลือกเรา
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              เราให้บริการค้นหาบริษัทรักษาความปลอดภัยที่ครบถ้วน เชื่อถือได้ และใช้งานง่าย
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card hover="lift" className="h-full text-center">
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-8 h-8 text-primary-600" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-neutral-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Companies Section */}
      <section className="py-16 bg-neutral-50">
        <div className="container-wrapper">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              บริษัทแนะนำ
            </h2>
            <p className="text-lg text-neutral-600">
              บริษัทรักษาความปลอดภัยที่ได้รับการแนะนำและมีคุณภาพสูง
            </p>
          </div>

          {featuredError ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">เกิดข้อผิดพลาดในการโหลดข้อมูลบริษัท</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                ลองใหม่
              </Button>
            </div>
          ) : featuredLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {[...Array(3)].map((_, index) => (
                <Card key={index} className="animate-pulse h-96">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 bg-neutral-200 rounded-xl"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-5 bg-neutral-200 rounded w-3/4"></div>
                          <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-neutral-200 rounded w-full"></div>
                        <div className="h-3 bg-neutral-200 rounded w-2/3"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-6 bg-neutral-200 rounded-full w-20"></div>
                        <div className="h-6 bg-neutral-200 rounded-full w-16"></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-6">
                        <div className="h-10 bg-neutral-200 rounded-lg"></div>
                        <div className="h-10 bg-neutral-200 rounded-lg"></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : featuredCompanies && featuredCompanies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {featuredCompanies.map((company, index) => (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="h-full"
                >
                  <CompanyCard company={company} showContactInfo={isAuthenticated && !isGuest} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-neutral-600">ไม่มีข้อมูลบริษัทแนะนำในขณะนี้</p>
            </div>
          )}

          <div className="text-center mt-12">
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                navigate('/search')
              }}
            >
              ดูบริษัททั้งหมด
            </Button>
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="py-16 bg-neutral-50">
        <div className="container-wrapper">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              เริ่มต้นการค้นหา
            </h2>
            <p className="text-lg text-neutral-600">
              เลือกวิธีการค้นหาที่เหมาะกับความต้องการของคุณ
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card hover="lift" className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <BuildingOffice2Icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">ตามชื่อบริษัท</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  ค้นหาด้วยชื่อบริษัทที่คุณต้องการ
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    navigate('/search?focusSearch=true')
                  }}
                >
                  ค้นหา
                </Button>
              </CardContent>
            </Card>

            <Card hover="lift" className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MapPinIcon className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">ตามพื้นที่</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  ค้นหาบริษัทในจังหวัดของคุณ
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    navigate('/search?showFilters=true&focusFilter=province')
                  }}
                >
                  เลือกพื้นที่
                </Button>
              </CardContent>
            </Card>

            <Card hover="lift" className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ShieldCheckIcon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-semibold mb-2">ตามบริการ</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  ค้นหาตามประเภทบริการที่ต้องการ
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    navigate('/search?showFilters=true&focusFilter=serviceType')
                  }}
                >
                  เลือกบริการ
                </Button>
              </CardContent>
            </Card>

            <Card hover="lift" className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <StarIcon className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="font-semibold mb-2">บริษัทแนะนำ</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  ดูบริษัทที่ได้รับการแนะนำ
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    navigate('/search?featured=true')
                  }}
                >
                  ดูบริษัทแนะนำ
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 🖼️ Advertisement - Koder3 */}
      <section className="py-8 bg-white">
        <div className="container-wrapper">
          <div className="flex justify-center">
            <CustomAdBanner
              type="in-feed"
              imageUrl="/ads/koder3-banner-square.jpg"
              targetUrl="https://www.koder3.com"
              alt="ระบบจัดการบริษัทรักษาความปลอดภัย - Koder3"
              className="max-w-3xl"
              showLabel={true}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <div className="container-wrapper text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              พร้อมเริ่มต้นแล้วหรือยัง?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              ค้นหาบริษัทรักษาความปลอดภัยที่เหมาะสมกับความต้องการของคุณตอนนี้
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="bg-white text-primary-600 hover:bg-neutral-50"
                onClick={() => {
                  navigate('/search')
                }}
              >
                เริ่มการค้นหา
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-primary-600"
                onClick={() => {
                  navigate('/about')
                }}
              >
                เรียนรู้เพิ่มเติม
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default HomePage